import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const SyncStatusPanel = ({ devices }) => {
  const activeDevices = devices?.filter(d => d?.isActive)?.length || 0;
  const totalDevices = devices?.length || 0;
  const recentSyncs = devices
    ?.filter(d => d?.lastSyncAt)
    ?.sort((a, b) => new Date(b?.lastSyncAt) - new Date(a?.lastSyncAt))
    ?.slice(0, 5);

  const syncHealthPercentage = totalDevices > 0 ? (activeDevices / totalDevices) * 100 : 0;

  const getHealthColor = () => {
    if (syncHealthPercentage >= 80) return 'text-success';
    if (syncHealthPercentage >= 50) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card rounded-xl p-6 elevation-2">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon name="Activity" size={24} className="text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Sync Status Overview</h2>
          <p className="text-sm text-muted-foreground">Monitor device synchronization health</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Sync Health */}
        <div className="bg-muted/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Sync Health</span>
            <Icon name="Heart" size={18} className={getHealthColor()} />
          </div>
          <div className="flex items-baseline space-x-2">
            <span className={`text-3xl font-bold ${getHealthColor()}`}>
              {syncHealthPercentage?.toFixed(0)}%
            </span>
          </div>
          <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              className={`h-full ${getHealthColor()?.replace('text-', 'bg-')}`}
              initial={{ width: 0 }}
              animate={{ width: `${syncHealthPercentage}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Active Devices */}
        <div className="bg-muted/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Active Devices</span>
            <Icon name="CheckCircle" size={18} className="text-success" />
          </div>
          <div className="flex items-baseline space-x-2">
            <span className="text-3xl font-bold text-foreground">{activeDevices}</span>
            <span className="text-sm text-muted-foreground">/ {totalDevices}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {activeDevices === totalDevices ? 'All devices active' : `${totalDevices - activeDevices} inactive`}
          </p>
        </div>

        {/* Data Points Today */}
        <div className="bg-muted/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Data Points Today</span>
            <Icon name="Database" size={18} className="text-primary" />
          </div>
          <div className="flex items-baseline space-x-2">
            <span className="text-3xl font-bold text-foreground">{activeDevices * 24}</span>
            <span className="text-sm text-success">+12%</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Compared to yesterday</p>
        </div>
      </div>
      {/* Recent Sync Activity */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Sync Activity</h3>
        <div className="space-y-3">
          {recentSyncs?.length > 0 ? (
            recentSyncs?.map((device) => (
              <div
                key={device?.id}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{device?.deviceName}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {device?.deviceType?.replace('_', ' ')}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-foreground">
                    {format(new Date(device?.lastSyncAt), 'MMM d, HH:mm')}
                  </p>
                  <p className="text-xs text-muted-foreground">Last synced</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Icon name="Clock" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No recent sync activity</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncStatusPanel;
